/**
 * How Tarifs et abonnements reads what the API answers (ADR-0018 §4, §4a).
 * Nothing here decides a price or a state: the server sends `inForce`,
 * `state`, `entitled` and `subscriberCount`, and this only groups and words
 * them.
 */
import type {
  ConsolePlan,
  ConsoleSubscription,
  ListPlanPricesResponse,
  PlanPriceVersion,
  ProDiscount,
  PromoState,
  ProviderEventResult,
} from "@wallyo/contracts/console/billing";
import type { SubscriptionEndReason, SubscriptionStatus } from "@wallyo/contracts/billing";
import type { WorkerCategory } from "@wallyo/contracts/types";
import { t } from "../../i18n";
import { formatCountry, formatDateTime, formatMoney, formatNumber, formatPercent } from "../../i18n/format";
import { tradeName } from "../customers/customer-format";

export type Tone = "ok" | "warn" | "bad" | "info" | "mute";

/** What the console prints where the API sent nothing. */
const NONE = "—";

/**
 * One list as a screen holds it. A read that fails with something older on
 * screen keeps it and says so, rather than emptying the table.
 */
export type BillingListState<T> =
  | { status: "loading"; data: T | null }
  | { status: "ready"; data: T }
  | { status: "failed"; data: T | null; error: unknown };

// ── Prix ──────────────────────────────────────────────────────────────────

/**
 * - `in_force`: what a new subscription in this country is charged now.
 * - `scheduled`: set for later; it starts applying at `validFrom`.
 * - `superseded`: an older version, kept so any renewal can be explained.
 */
export type PriceState = "in_force" | "scheduled" | "superseded";

export type PriceRow = { price: PlanPriceVersion; state: PriceState };

/** One plan's price versions in one country, the newest first. */
export type PlanPriceGroup = {
  feeTierId: string;
  /** Null when the price names a plan this build doesn't know. */
  plan: ConsolePlan | null;
  rows: PriceRow[];
};

export type CountryPriceGroup = { country: string; plans: PlanPriceGroup[] };

export function priceState(price: PlanPriceVersion, now: Date): PriceState {
  if (price.inForce) return "in_force";
  if (Date.parse(price.validFrom) > now.getTime()) return "scheduled";
  return "superseded";
}

export const PRICE_STATE_TONE: Record<PriceState, Tone> = {
  in_force: "ok",
  scheduled: "info",
  superseded: "mute",
};

/**
 * The prices by country, then by plan, each plan's versions newest first.
 * Countries come in the staff member's alphabet; plans in the order the
 * server listed them.
 */
export function groupPlanPrices(response: ListPlanPricesResponse, now: Date): CountryPriceGroup[] {
  const plans = new Map(response.plans.map((plan) => [plan.feeTierId, plan]));
  const planOrder = response.plans.map((plan) => plan.feeTierId);
  const byCountry = new Map<string, Map<string, PriceRow[]>>();
  for (const price of response.prices) {
    const country = price.country.toUpperCase();
    const inCountry = byCountry.get(country) ?? new Map<string, PriceRow[]>();
    byCountry.set(country, inCountry);
    inCountry.set(price.feeTierId, [
      ...(inCountry.get(price.feeTierId) ?? []),
      { price, state: priceState(price, now) },
    ]);
  }
  const rank = (feeTierId: string) => {
    const index = planOrder.indexOf(feeTierId);
    if (index === -1) return planOrder.length;
    return index;
  };
  return [...byCountry]
    .sort(([left], [right]) => formatCountry(left).localeCompare(formatCountry(right)))
    .map(([country, inCountry]) => ({
      country,
      plans: [...inCountry]
        .sort(([left], [right]) => rank(left) - rank(right))
        .map(([feeTierId, rows]) => ({
          feeTierId,
          plan: plans.get(feeTierId) ?? null,
          rows: [...rows].sort((left, right) => Date.parse(right.price.validFrom) - Date.parse(left.price.validFrom)),
        })),
    }));
}

/**
 * The plans with no price anywhere yet. A pro in a country with no price
 * can't subscribe, and the server refuses a promo on one (`plan_not_priced`),
 * so the screen names them rather than leaving staff to guess.
 */
export function unpricedPlans(response: ListPlanPricesResponse): ConsolePlan[] {
  const priced = new Set(response.prices.map((price) => price.feeTierId));
  return response.plans.filter((plan) => !priced.has(plan.feeTierId));
}

/** "Mensuelle (par mois)", or the tier id when this build doesn't know the plan. */
export function planLabel(plan: ConsolePlan | null, feeTierId: string): string {
  if (!plan) return feeTierId;
  return t("billing.planWithInterval", { name: plan.name, interval: t(`billing.interval.${plan.interval}` as const) });
}

// ── Promos et remises ────────────────────────────────────────────────────

export const PROMO_STATE_TONE: Record<PromoState, Tone> = {
  scheduled: "info",
  running: "ok",
  finished: "mute",
  ended_early: "warn",
};

export const promoStateName = (state: PromoState): string => t(`billing.state.${state}` as const);

/** A promo or discount staff can still stop: it is running, or hasn't started. */
export function canEndEarly(offer: { state: PromoState }): boolean {
  return offer.state === "running" || offer.state === "scheduled";
}

/** "3 périodes payées", or "tant qu'il reste abonné" when there is no limit. */
export function periodsLabel(periods: number | null): string {
  if (periods === null) return t("billing.periods.whileSubscribed");
  return t("billing.periods.count", { count: formatNumber(periods) });
}

/** "du 1 oct. 2026, 00:00 au 31 oct. 2026, 23:59", plus when it was stopped early. */
export function windowLabel(offer: { startsAt: string; endsAt: string; endedAt: string | null }): string {
  const window = t("billing.window", { from: formatDateTime(offer.startsAt), to: formatDateTime(offer.endsAt) });
  if (!offer.endedAt) return window;
  return `${window} · ${t("billing.window.endedEarly", { date: formatDateTime(offer.endedAt) })}`;
}

/** Who a discount is for: the pro, a country, or a trade. */
export function discountTarget(discount: ProDiscount): string {
  switch (discount.scope) {
    case "pro":
      return discount.proName ?? discount.workerId ?? NONE;
    case "country":
      if (!discount.country) return NONE;
      return formatCountry(discount.country);
    case "category":
      if (!discount.category) return NONE;
      return tradeName(discount.category as WorkerCategory);
  }
}

/** "20 %" off the country's regular price. */
export const percentOffLabel = (percentOff: number): string => formatPercent(percentOff / 100);

/** A discount's plan, or "Any plan" when it applies to any. */
export function discountPlanLabel(discount: ProDiscount): string {
  return discount.planName ?? t("billing.anyPlan");
}

// ── Un abonnement ────────────────────────────────────────────────────────

export const SUBSCRIPTION_STATUS_TONE: Record<SubscriptionStatus, Tone> = {
  pending: "warn",
  active: "ok",
  ending: "info",
  suspended: "bad",
  ended: "mute",
};

export const subscriptionStatusName = (status: SubscriptionStatus): string =>
  t(`billing.subscription.${status}` as const);

export const endReasonName = (reason: SubscriptionEndReason): string => t(`billing.endReason.${reason}` as const);

export const PROVIDER_EVENT_TONE: Record<ProviderEventResult, Tone> = {
  applied: "ok",
  conflict: "bad",
  ignored: "mute",
};

/** "25 000 GNF", and the regular price beside it when a promo or remise lowered it. */
export function priceNowLabel(subscription: ConsoleSubscription): string {
  const now = formatMoney(subscription.priceAmount, subscription.currency);
  if (subscription.priceAmount >= subscription.regularAmount) return now;
  return t("billing.priceInsteadOf", {
    now,
    regular: formatMoney(subscription.regularAmount, subscription.currency),
  });
}

/** "Promo Lancement Conakry · 2 périodes restantes", or null when the price is the regular one. */
export function reductionLabel(subscription: ConsoleSubscription): string | null {
  if (!subscription.reductionName) return null;
  if (subscription.reducedPeriodsLeft === null) return subscription.reductionName;
  return t("billing.reductionWithPeriods", {
    name: subscription.reductionName,
    count: formatNumber(subscription.reducedPeriodsLeft),
  });
}

/** Finance can move `paidThrough` later while the subscription is still alive. */
export function canExtend(subscription: ConsoleSubscription): boolean {
  const status = subscription.status;
  return status === "active" || status === "ending" || status === "suspended";
}

/** Anything not already over can be ended now. */
export function canEndNow(subscription: ConsoleSubscription): boolean {
  return subscription.status !== "ended";
}
